import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, IconButton, Tooltip, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Papa from 'papaparse';
import { CampaignProspectType } from '../../api/campaigns';
import * as spreadsheetApi from '../../api/spreadsheet';
import type { Column } from '../../api/spreadsheet';
import {
  useCampaignContextController,
  useSpreadsheetViewType
} from '../../context/CampaignContext';
import { bumpSpreadsheetDataVersion } from '../../context/CampaignContext';
import { useSnackbar } from '../../context/SnackbarContext';
import ImportProspectsModal from './ImportProspectsModal';
import CSVUploadModal from './CSVUploadModal';
import CRMImportModal from './CRMImportModal';
import ScrapeWebsiteImportModal from './ScrapeWebsiteImportModal';
import ProspectDatabaseImportModal from './ProspectDatabaseImportModal';
import { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import { MAX_SPREADSHEET_ROWS } from '../spreadsheet/spreadsheetConstants';
import type { SpreadsheetRefHandle } from '../spreadsheet/Spreadsheet';

interface CampaignActionsProps {
  prospectType?: CampaignProspectType;
  onImportClick?: () => void;
  onExportClick?: () => void;
  onImportOptionSelect?: (option: string) => void;
  /** When false, Import button is disabled (read-only mode). */
  canEdit?: boolean;
  spreadsheetRef?: React.RefObject<SpreadsheetRefHandle | null>;
  /** Row indices currently hidden by the sheet filter; export will exclude these rows. */
  getFilteredOutRowIds?: () => number[] | null;
}

const CampaignActions: React.FC<CampaignActionsProps> = ({
  prospectType = CampaignProspectType.OPPORTUNITY,
  onImportClick,
  onExportClick,
  onImportOptionSelect,
  canEdit = true,
  spreadsheetRef,
  getFilteredOutRowIds
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [state, dispatch] = useCampaignContextController();
  const viewType = useSpreadsheetViewType();
  const campaignName = state.currentCampaign?.name;
  const { showSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);
  const [crmImportModalOpen, setCrmImportModalOpen] = useState(false);
  const [scrapeWebsiteModalOpen, setScrapeWebsiteModalOpen] = useState(false);
  const [prospectDatabaseModalOpen, setProspectDatabaseModalOpen] =
    useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportClick = async () => {
    if (!campaignId) {
      showSnackbar('Campaign not found', 'error');
      return;
    }
    setExporting(true);
    try {
      const [columnsResponse, { cells }] = await Promise.all([
        spreadsheetApi.getColumns(campaignId, viewType),
        spreadsheetApi.getCells(campaignId, viewType)
      ]);
      const columns: Column[] = (columnsResponse.columns || []).sort(
        (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
      );
      const columnNames = columns.map((c) => c.col_name);

      let rowIds = Array.from(new Set(cells.map((c) => c.row_id))).sort(
        (a, b) => a - b
      );
      const filteredOut = getFilteredOutRowIds?.();
      if (Array.isArray(filteredOut) && filteredOut.length > 0) {
        const hiddenSet = new Set(filteredOut);
        rowIds = rowIds.filter((id) => !hiddenSet.has(id));
      }
      const cellByKey = new Map<string, string>();
      for (const cell of cells) {
        const key = `${cell.row_id}\t${cell.column_name}`;
        cellByKey.set(key, cell.cell_value ?? '');
      }

      const rows: Record<string, string>[] = rowIds.map((rowId) => {
        const row: Record<string, string> = {};
        for (const colName of columnNames) {
          row[colName] = cellByKey.get(`${rowId}\t${colName}`) ?? '';
        }
        return row;
      });

      const csv = Papa.unparse(rows, { header: true, columns: columnNames });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (campaignName ?? campaignId ?? 'campaign').replace(
        /[^a-zA-Z0-9-_]/g,
        '-'
      );
      link.setAttribute('download', `campaign-${safeName}-export.csv`);
      link.click();
      URL.revokeObjectURL(url);
      showSnackbar(
        rows.length === 0
          ? 'No data to download'
          : `Downloaded ${rows.length} row(s) as CSV`,
        rows.length === 0 ? 'info' : 'success'
      );
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to download table', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    if (onImportClick) {
      onImportClick();
    } else {
      setModalOpen(true);
    }
  };

  const handleOptionSelect = (option: string) => {
    if (option === 'upload_csv') {
      setCsvUploadModalOpen(true);
    } else if (option === 'import_crm') {
      setCrmImportModalOpen(true);
    } else if (option === 'scrape_website') {
      setScrapeWebsiteModalOpen(true);
    } else if (option === 'search_and_enrich') {
      setProspectDatabaseModalOpen(true);
    }

    if (onImportOptionSelect) {
      onImportOptionSelect(option);
    } else if (
      option !== 'upload_csv' &&
      option !== 'import_crm' &&
      option !== 'scrape_website' &&
      option !== 'search_and_enrich'
    ) {
      console.log('Import option selected:', option);
    }
  };

  const handleFileSelect = (_file: File) => {
    // File is handled by CSVUploadModal; no extra action needed
  };

  const handleMappingComplete = (
    uploadedRows: Record<string, any>[],
    _mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => {
    if (!campaignId) {
      showSnackbar('Campaign not found', 'error');
      return;
    }
    if (uploadedRows.length === 0) {
      showSnackbar('No rows to import', 'warning');
      return;
    }
    const rowCount =
      spreadsheetRef?.current?.appendRowsToSpreadsheetLocal(
        uploadedRows,
        customColumnTypes
      ) ?? 0;
    if (rowCount > 0) {
      // bumpSpreadsheetDataVersion(dispatch);
      showSnackbar(`Added ${rowCount} row(s) to the spreadsheet`, 'success');
    } else if (rowCount === -1) {
      showSnackbar(
        `Spreadsheet is limited to ${MAX_SPREADSHEET_ROWS} rows. Import failed.`,
        'error'
      );
    } else {
      showSnackbar(
        'Could not add rows to spreadsheet. Ensure the spreadsheet is loaded.',
        'warning'
      );
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: '16px' }} />}
          onClick={handleImportClick}
          disabled={!canEdit}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            padding: '2px 10px',
            fontSize: '13px',
            fontWeight: 500,
            minWidth: 'auto',
            borderColor: (theme) => theme.palette.divider,
            color: (theme) => theme.palette.text.primary,
            transition: 'all 0.2s ease',
            '& .MuiButton-startIcon': {
              marginRight: '6px',
              marginLeft: 0
            },
            '&:hover': {
              borderColor: (theme) =>
                prospectType === CampaignProspectType.ACCOUNT
                  ? theme.customColors.spreadsheetAccountAccent
                  : theme.customColors.themePrimary,
              backgroundColor: (theme) => theme.palette.action.hover,
              color: (theme) =>
                prospectType === CampaignProspectType.ACCOUNT
                  ? theme.customColors.spreadsheetAccountAccent
                  : theme.customColors.themePrimary,
              transform: 'translateY(-1px)'
            }
          }}
        >
          Import Prospects
        </Button>
        <Tooltip title="Download to CSV" arrow placement="bottom">
          <IconButton
            size="small"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              backgroundColor: 'transparent',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
                color: (theme) =>
                  prospectType === CampaignProspectType.ACCOUNT
                    ? theme.customColors.spreadsheetAccountAccent
                    : theme.customColors.themePrimary,
                transform: 'translateY(-1px)'
              }
            }}
            onClick={onExportClick ?? handleExportClick}
            disabled={exporting}
          >
            <FileDownloadIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <ImportProspectsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prospectType={prospectType}
        onOptionSelect={handleOptionSelect}
      />
      <CSVUploadModal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        onFileSelect={handleFileSelect}
        onMappingComplete={handleMappingComplete}
      />
      <CRMImportModal
        open={crmImportModalOpen}
        onClose={() => setCrmImportModalOpen(false)}
        prospectType={prospectType}
        onMappingComplete={handleMappingComplete}
      />
      <ScrapeWebsiteImportModal
        open={scrapeWebsiteModalOpen}
        onClose={() => setScrapeWebsiteModalOpen(false)}
        prospectType={prospectType}
        onMappingComplete={handleMappingComplete}
      />
      <ProspectDatabaseImportModal
        open={prospectDatabaseModalOpen}
        onClose={() => setProspectDatabaseModalOpen(false)}
        prospectType={prospectType}
        onMappingComplete={handleMappingComplete}
      />
    </>
  );
};

export default CampaignActions;
